import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import toast from "react-hot-toast";
import Layout from "../../../../components/Layout";
import OrderDispatch from "../../../../components/OrderDispatch";
import { get, post } from "../../../../services/ApiService";
import "./studio.css";

const CATEGORIES = [
  { id: "running", label: "Running", hint: "Still sending" },
  { id: "to-print", label: "To print", hint: "Labels waiting" },
  { id: "printed", label: "Printed", hint: "Labels downloaded" },
];

const FORMAT_ORDER = ["Parcel", "Letter", "Large letter", "undefined"];

const outcomeLabel = {
  sent: "Sent",
  label: "Label issue",
  failed: "Failed",
  waiting: "Waiting",
};

const todayDate = () => new Date().toISOString().split("T")[0];

const clock = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const carrierOf = (type) => {
  if (type === "RM_ORDERS") return "Royal Mail";
  if (type === "AMAZON_ORDERS") return "Amazon";
  return type || "Courier";
};

const outcomeOf = (row) => {
  if (row.success && row.error) return "label";
  if (row.success) return "sent";
  if (row.error) return "failed";
  return "waiting";
};

const categoryOf = (job) => {
  if (job.status === "IN_PROGRESS") return "running";
  const labels = job.labels || [];
  if (job.status !== "CLEARED" && labels.some((label) => !label.isPrinted)) return "to-print";
  return "printed";
};

const mapBatches = (batches) =>
  (batches || []).flatMap((batch) =>
    (batch.jobs || []).filter((job) => Number(job.totalOrders) > 0).map((job) => ({
      id: String(job._id),
      category: categoryOf(job),
      carrier: carrierOf(job.type),
      type: job.type,
      domain: job.domain || "",
      status: job.status,
      startedAt: clock(job.startedAt),
      finishedAt: job.finishedAt ? clock(job.finishedAt) : null,
      totalOrders: job.totalOrders || 0,
      created: job.created || 0,
      failed: job.failed || 0,
      stats: job.stats || {},
      formats: FORMAT_ORDER.filter((format) => Number(job.stats?.[format]?.total) > 0),
      labels: (job.labels || []).map((label) => ({
        ...label,
        jobId: job._id,
        domain: job.domain,
      })),
      results: Array.isArray(job.results) ? job.results : [],
      isMerge: Boolean(batch.isMerge),
    }))
  );

const ICON_PATHS = {
  refresh: <><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></>,
  printer: <><path d="M6 9V3h12v6" /><rect x="4" y="9" width="16" height="8" rx="2" /><path d="M6 14h12v7H6z" /></>,
  check: <path d="M5 12l5 5L20 7" />,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M8 3v4" /><path d="M16 3v4" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>,
  file: <><path d="M6 2h9l5 5v15H6z" /><path d="M14 2v6h6" /><path d="M9 13h6" /><path d="M9 17h6" /></>,
  download: <><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 21h16" /></>,
  inbox: <><path d="M4 6h16v12H4z" /><path d="M4 10h16" /><path d="M8 14h4" /></>,
  x: <><path d="M6 6l12 12" /><path d="M18 6L6 18" /></>,
};

const Icon = ({ name, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={size <= 12 ? 3 : 2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {ICON_PATHS[name]}
  </svg>
);

const CourierJobsStudio = () => {
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [category, setCategory] = useState("running");
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [outcome, setOutcome] = useState("all");
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingLabels, setLoadingLabels] = useState({});
  const [clearingId, setClearingId] = useState(null);
  const [reprintLabel, setReprintLabel] = useState(null);
  const loadRef = useRef(async () => false);

  useEffect(() => {
    let stopped = false;
    let timer;
    const request = { id: 0 };

    const load = async (showSpinner) => {
      const id = ++request.id;
      if (showSpinner) setIsLoading(true);
      else setIsRefreshing(true);

      try {
        const res = await get(`api/orders/courier/jobs?date=${selectedDate}`);
        if (stopped || id !== request.id) return false;
        if (!res.success) {
          setLoadError(res.message || "Could not load courier jobs");
          return false;
        }
        const next = [
          ...mapBatches(res.inProgress),
          ...mapBatches(res.notPrinted),
          ...mapBatches(res.printed),
        ];
        setJobs(next);
        setLoadError("");
        setLastUpdated(new Date());
        return next.some((job) => job.status === "IN_PROGRESS");
      } catch {
        if (!stopped && id === request.id) setLoadError("Could not load courier jobs");
        return false;
      } finally {
        if (!stopped && id === request.id) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    const schedule = (delay) => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        if (stopped) return;
        if (document.hidden) {
          schedule(1000);
          return;
        }
        const running = await load(false);
        if (!stopped) schedule(running ? 3000 : 15000);
      }, delay);
    };

    loadRef.current = () => load(false);
    load(true).then((running) => {
      if (!stopped) schedule(running ? 3000 : 15000);
    });

    const onVisible = () => {
      if (document.hidden || stopped) return;
      load(false).then((running) => {
        if (!stopped) schedule(running ? 3000 : 15000);
      });
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [selectedDate]);

  const counts = useMemo(
    () =>
      CATEGORIES.reduce((map, item) => {
        map[item.id] = jobs.filter((job) => job.category === item.id).length;
        return map;
      }, {}),
    [jobs]
  );

  const jobsInCategory = jobs.filter((job) => job.category === category).filter((job) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return (
      job.carrier.toLowerCase().includes(needle) ||
      job.domain.toLowerCase().includes(needle) ||
      job.results.some((row) => String(row.orderId || "").toLowerCase().includes(needle)) ||
      job.labels.some((label) => String(label.fileName || "").toLowerCase().includes(needle))
    );
  });

  const selected = jobsInCategory.find((job) => job.id === selectedId) || jobsInCategory[0] || null;

  const results = (selected?.results || []).filter((row) => {
    const needle = query.trim().toLowerCase();
    const matchesQuery = !needle || String(row.orderId || "").toLowerCase().includes(needle);
    const matchesOutcome = outcome === "all" || outcomeOf(row) === outcome;
    return matchesQuery && matchesOutcome;
  });

  const chooseCategory = (next) => {
    setCategory(next);
    setOutcome("all");
    const first = jobs.find((job) => job.category === next);
    setSelectedId(first ? first.id : null);
  };

  const changeDate = (value) => {
    setSelectedDate(value);
    setSelectedId(null);
    setOutcome("all");
    setJobs([]);
    setIsLoading(true);
  };

  const downloadFile = async (blob, fileName = "shipping-label") => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const apiBase = (value) => String(value || "").trim().replace(/^["']+|["']+$/g, "");

  const downloadLabel = async (label) => {
    if (!label?.filePath || loadingLabels[label.filePath]) return;

    try {
      setLoadingLabels((prev) => ({ ...prev, [label.filePath]: true }));
      const baseUrl = apiBase(
        String(label.domain || "").toLowerCase() === "external"
          ? process.env.REACT_APP_EXT_API_ENDPOINT
          : process.env.REACT_APP_API_ENDPOINT
      );
      const response = await fetch(`${baseUrl}/api/orders/label/download`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ filePath: label.filePath }),
      });

      if (!response.ok) {
        toast.error("Download failed, please try again.");
        return;
      }

      const blob = await response.blob();
      await downloadFile(blob, label.fileName || "shipping-label");

      if (!label.isPrinted) {
        const printRes = await post("api/orders/labels/mark-printed", {
          jobId: label.jobId,
          filePath: label.filePath,
        });
        if (printRes.success) {
          setJobs((prev) =>
            prev.map((job) => {
              if (job.id !== String(label.jobId)) return job;
              const labels = job.labels.map((item) =>
                item.filePath === label.filePath ? { ...item, isPrinted: true } : item
              );
              return { ...job, labels, category: categoryOf({ ...job, labels }) };
            })
          );
          loadRef.current();
        } else {
          toast.error("Failed to mark label as printed");
        }
      }
    } catch {
      toast.error("Download failed, please try again.");
    } finally {
      setLoadingLabels((prev) => ({ ...prev, [label.filePath]: false }));
    }
  };

  const handleLabelClick = (label) => {
    if (!label?.filePath || loadingLabels[label.filePath]) return;
    if (label.isPrinted) {
      setReprintLabel(label);
      return;
    }
    downloadLabel(label);
  };

  const confirmReprint = () => {
    const label = reprintLabel;
    setReprintLabel(null);
    if (label) downloadLabel(label);
  };

  const handleClearJob = async (jobId) => {
    try {
      setClearingId(jobId);
      const res = await post("api/orders/courier/jobs/clear", { jobId });
      if (res.success) {
        toast.success("Job cleared successfully");
        loadRef.current();
      } else {
        toast.error(res.error || "Failed to clear job");
      }
    } catch {
      toast.error("Failed to clear job");
    } finally {
      setClearingId(null);
    }
  };

  const domainLabel = (domain) => (domain === "external" ? "External" : "Local");

  const statusLabel = (job) => {
    if (job.status === "FAILED") return "Failed";
    if (job.status === "CLEARED") return "Cleared";
    return CATEGORIES.find((item) => item.id === job.category)?.label;
  };

  const statusClass = (job) => (job.status === "FAILED" ? "failed" : job.category);

  const categoryIcon = (id) => {
    if (id === "running") return <Icon name="refresh" />;
    if (id === "to-print") return <Icon name="printer" />;
    return <Icon name="check" />;
  };

  const jobProgress = (job) => {
    const printedCount = job.labels.filter((label) => label.isPrinted).length;
    if (job.category === "running") {
      const pct = job.totalOrders > 0 ? Math.round((job.created / job.totalOrders) * 100) : 0;
      return { pct, text: `${job.created}/${job.totalOrders} sent`, tone: "running" };
    }
    const total = job.labels.length;
    const pct = total > 0 ? Math.round((printedCount / total) * 100) : job.category === "printed" ? 100 : 0;
    const tone = total > 0 && printedCount === total ? "printed" : "to-print";
    return { pct, text: `${printedCount}/${total} printed`, tone };
  };

  const readyLabels = selected ? selected.labels.filter((label) => !label.isPrinted).length : 0;
  const printedLabels = selected ? selected.labels.filter((label) => label.isPrinted).length : 0;

  return (
    <Layout>
      <div className="cj-studio">
        <header className="cj-top">
          <div className="cj-heading">
            <p className="cj-kicker">Dispatch</p>
            <h1>Courier jobs</h1>
            <p className="cj-sub">Running sends, labels still to print, and labels already printed.</p>
          </div>
          <div className="cj-tools">
            <label className="cj-date">
              <Icon name="calendar" />
              <span>Date</span>
              <input
                type="date"
                value={selectedDate}
                onChange={(event) => changeDate(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="cj-btn"
              onClick={() => loadRef.current()}
              disabled={isRefreshing}
            >
              <Icon name="refresh" />
              <span>{isRefreshing ? "Refreshing…" : "Refresh"}</span>
              <span className="cj-updated">
                {lastUpdated ? `· ${clock(lastUpdated)}` : ""}
              </span>
            </button>
            <div className="cj-dispatch">
              <OrderDispatch openJobsInNewTab={false} />
            </div>
          </div>
        </header>

        <div className="cj-categories" role="tablist" aria-label="Job status">
          {CATEGORIES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={category === item.id}
              className={`cj-category cj-category-${item.id}${category === item.id ? " is-active" : ""}`}
              onClick={() => chooseCategory(item.id)}
            >
              <div className="cj-category-text">
                <span>{item.label}</span>
                <strong>{counts[item.id] || 0}</strong>
                <small>{item.hint}</small>
              </div>
              <div className="cj-category-icon">{categoryIcon(item.id)}</div>
            </button>
          ))}
        </div>

        <div className="cj-board">
          <section className="cj-panel cj-jobs" aria-label="Jobs">
            <div className="cj-panel-head">
              <h2>
                {CATEGORIES.find((item) => item.id === category)?.label}
                <span className="cj-count">
                  {" "}· {jobsInCategory.length} {jobsInCategory.length === 1 ? "job" : "jobs"}
                </span>
              </h2>
              <label className="cj-search">
                <Icon name="search" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Order, carrier, or file"
                  aria-label="Search jobs"
                />
              </label>
            </div>
            <div className="cj-job-list">
              {isLoading && jobs.length === 0 && <div className="cj-empty">Loading jobs…</div>}
              {!isLoading && loadError && <div className="cj-empty">{loadError}</div>}
              {!isLoading && !loadError && jobsInCategory.length === 0 && (
                <div className="cj-empty">No jobs found for this date.</div>
              )}
              {jobsInCategory.map((job) => {
                const progress = jobProgress(job);
                return (
                  <button
                    key={job.id}
                    type="button"
                    className={`cj-job${selected?.id === job.id ? " is-selected" : ""}`}
                    aria-current={selected?.id === job.id ? "true" : undefined}
                    onClick={() => {
                      setSelectedId(job.id);
                      setOutcome("all");
                    }}
                  >
                    <div className="cj-job-row">
                      <h3>{job.carrier}</h3>
                      <span className={`cj-pill domain ${job.domain === "external" ? "external" : "local"}`}>
                        {domainLabel(job.domain)}
                      </span>
                      {job.status === "FAILED" || job.status === "CLEARED" ? (
                        <span className={`cj-pill ${statusClass(job)}`}>{statusLabel(job)}</span>
                      ) : null}
                      <span className="cj-job-time">
                        {job.startedAt || ""}
                        {job.finishedAt ? `–${job.finishedAt}` : job.startedAt ? "–" : ""}
                      </span>
                    </div>
                    <div className="cj-progress-row">
                      <div className={`cj-progress ${progress.tone}`}>
                        <span style={{ width: `${progress.pct}%` }} />
                      </div>
                      <span className={`cj-progress-text ${progress.tone}`}>{progress.text}</span>
                    </div>
                    <div className="cj-metrics">
                      <span><b>{job.totalOrders}</b> orders</span>
                      <span><b>{job.created}</b> sent</span>
                      <span className={job.failed > 0 ? "has-failed" : ""}><b>{job.failed}</b> failed</span>
                      {job.formats.length > 0 && <span className="cj-metric-formats">{job.formats.join(", ")}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {!selected && !isLoading && (
            <div className="cj-panel cj-empty cj-empty-panel">Select a job to see its orders.</div>
          )}

          {selected && (
            <section className="cj-detail" aria-label="Job detail">
              <div className="cj-panel cj-detail-head">
                <div className="cj-detail-bar">
                  <div className="cj-detail-title">
                    <div className="cj-title-row">
                      <h2>{selected.carrier}</h2>
                      <span className={`cj-pill domain ${selected.domain === "external" ? "external" : "local"}`}>
                        {domainLabel(selected.domain)}
                      </span>
                      <span className={`cj-pill status ${statusClass(selected)}`}>
                        {statusClass(selected) === "printed" && <Icon name="check" size={12} />}
                        {statusLabel(selected)}
                      </span>
                    </div>
                    <div className="cj-meta">
                      <span>{selected.type === "RM_ORDERS" ? "Click & Drop" : "Buy Shipping"}</span>
                      {selected.isMerge && <span>Merged batch</span>}
                      {selected.startedAt && (
                        <span>
                          {selected.startedAt}
                          {selected.finishedAt ? `–${selected.finishedAt}` : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  {selected.failed > 0 && selected.status !== "CLEARED" && (
                    <div className="cj-detail-actions">
                      <button
                        type="button"
                        className="cj-btn cj-btn-danger"
                        disabled={clearingId === selected.id}
                        onClick={() => handleClearJob(selected.id)}
                      >
                        <Icon name="x" />
                        <span>{clearingId === selected.id ? "Clearing…" : "Clear failed job"}</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="cj-stat-row">
                  <div className="cj-stat"><span>Orders</span><strong>{selected.totalOrders}</strong></div>
                  <div className="cj-stat"><span>Sent</span><strong className="is-green">{selected.created}</strong></div>
                  <div className="cj-stat"><span>Failed</span><strong className={selected.failed > 0 ? "is-red" : ""}>{selected.failed}</strong></div>
                  <div className="cj-stat"><span>Results</span><strong>{selected.results.length}</strong></div>
                </div>
              </div>

              <div className="cj-panel cj-formats">
                {FORMAT_ORDER.map((format) => {
                  const stats = selected.stats?.[format] || { total: 0, created: 0, failed: 0 };
                  const quiet = !Number(stats.total);
                  return (
                    <div className={`cj-format${quiet ? " is-quiet" : ""}`} key={format}>
                      <div className="cj-format-name">
                        <span>{format}</span>
                      </div>
                      <div className="cj-format-counts">
                        <span title="Sent"><b>{stats.created || 0}</b> sent</span>
                        <span title="Failed"><b>{stats.failed || 0}</b> failed</span>
                        <span title="Total"><b>{stats.total || 0}</b> total</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="cj-panel cj-files">
                <div className="cj-section-head">
                  <div className="cj-section-title">
                    <h3>Label files</h3>
                    <span className="cj-chip printed">{printedLabels} printed</span>
                    <span className={`cj-chip ${readyLabels > 0 ? "ready" : "quiet"}`}>{readyLabels} to print</span>
                  </div>
                </div>
                {selected.labels.length === 0 && (
                  <div className="cj-file-empty">No label file yet. This job is still running.</div>
                )}
                {selected.labels.map((label) => {
                  const printed = Boolean(label.isPrinted);
                  const busy = Boolean(loadingLabels[label.filePath]);
                  return (
                    <button
                      key={`${label.jobId}-${label.filePath}`}
                      type="button"
                      className={`cj-file${printed ? " is-printed" : " is-ready"}`}
                      onClick={() => handleLabelClick(label)}
                      disabled={busy}
                      title={printed ? "Print again" : "Download and mark as printed"}
                    >
                      <div className={`cj-file-icon${printed ? " is-printed" : " is-ready"}`}>
                        <Icon name="file" />
                      </div>
                      <div className="cj-file-text">
                        <div className="cj-file-name">{label.fileName || label.filePath}</div>
                        <div className="cj-file-meta">
                          {label.carrier || "PDF"} · {printed ? "Printed" : "Waiting to be printed"}
                        </div>
                      </div>
                      <span className={`cj-file-status${printed ? " is-printed" : " is-ready"}`}>
                        {printed && !busy && <Icon name="check" size={12} />}
                        {busy ? "Downloading" : printed ? "Printed" : "Ready to print"}
                      </span>
                      <span className="cj-file-action" aria-hidden="true">
                        <Icon name={printed ? "printer" : "download"} />
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="cj-panel cj-results">
                <div className="cj-section-head">
                  <h3>Order results</h3>
                  <div className="cj-filters" role="group" aria-label="Filter results">
                    {[
                      ["all", "All"],
                      ["sent", "Sent"],
                      ["label", "Label issue"],
                      ["failed", "Failed"],
                      ["waiting", "Waiting"],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={outcome === id}
                        className={outcome === id ? "is-active" : ""}
                        onClick={() => setOutcome(id)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cj-table-wrap">
                  <table className="cj-table">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((row, index) => {
                        const kind = outcomeOf(row);
                        return (
                          <tr key={`${row.orderId || "order"}-${index}`}>
                            <td className="cj-order">{row.orderId || "—"}</td>
                            <td>
                              <span className={`cj-pill ${kind === "sent" ? "ok" : kind === "waiting" ? "wait" : kind === "label" ? "to-print" : "bad"}`}>
                                {outcomeLabel[kind]}
                              </span>
                            </td>
                            <td className={row.error ? "cj-error has-error" : "cj-error"}>
                              {row.error || (kind === "waiting" ? "Still in this send" : "Accepted")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {selected.results.length === 0 && (
                  <div className="cj-results-empty">
                    <div className="cj-results-empty-icon"><Icon name="inbox" /></div>
                    <strong>No per-order results stored for this job</strong>
                    <span>
                      {selected.domain === "external"
                        ? "External jobs return results once the courier confirms. Refresh after a few minutes."
                        : "Results appear here once the send finishes."}
                    </span>
                  </div>
                )}
                {selected.results.length > 0 && results.length === 0 && (
                  <div className="cj-results-empty">
                    <strong>No orders match this filter</strong>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      <Modal
        open={Boolean(reprintLabel)}
        onCancel={() => setReprintLabel(null)}
        footer={null}
        centered
        destroyOnClose
        width={440}
        className="cj-reprint-modal"
      >
        <div className="cj-reprint">
          <div className="cj-reprint-icon">
            <Icon name="printer" size={24} />
          </div>
          <h4>Print this label again?</h4>
          <p>This label has already been printed. Printing it again will download the same PDF.</p>
          {reprintLabel ? (
            <div className="cj-reprint-file">
              <span className="cj-reprint-file-icon"><Icon name="file" /></span>
              <span className="cj-reprint-file-text">
                <span className="cj-reprint-file-name">{reprintLabel.fileName || reprintLabel.filePath}</span>
                <span className="cj-reprint-file-meta">{reprintLabel.carrier || "PDF"} · Printed</span>
              </span>
              <span className="cj-pill printed"><Icon name="check" size={12} />Printed</span>
            </div>
          ) : null}
          <div className="cj-reprint-actions">
            <button type="button" className="cj-btn" onClick={() => setReprintLabel(null)}>
              Cancel
            </button>
            <button type="button" className="cj-btn cj-btn-primary" onClick={confirmReprint}>
              <Icon name="printer" />
              <span>Print again</span>
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default CourierJobsStudio;
